import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Navbar, Nav, Button, Spinner, Alert, Badge } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { getCachedAccountData } from '../../shared/accountCache';
import CurrencyManagement from './CurrencyManagement';
import apiCall from '../../shared/apiCall';
import SlidingMenu from '../../components/SlidingMenu';
import VersionFooter from '../../components/VersionFooter';

export default function AdminDashboard() {
  const { t } = useTranslation();
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const [accountData, setAccountData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [datePeriod, setDatePeriod] = useState('all'); // 'today', 'week', 'month', 'all'
  const [userStats, setUserStats] = useState(null);
  const [planStats, setPlanStats] = useState(null);
  const [payingUsersStats, setPayingUsersStats] = useState(null);
  const [error, setError] = useState('');

  // Note: Authentication is handled by ProtectedRoute wrapper, so currentUser is guaranteed to exist
  useEffect(() => {
    // Fetch account data to check subscription tier
    fetchAccountData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch stats when period changes
  useEffect(() => {
    if (accountData) {
      fetchStats();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [datePeriod, accountData]);

  const fetchAccountData = async () => {
    try {
      setLoading(true);
      const account = await getCachedAccountData();
      
      if (account) {
        // Check if user has admin tier
        if (account.subscription?.tier !== 'admin') {
          // Redirect to home if not admin
          navigate('/');
          return;
        }
        setAccountData(account);
      } else {
        // No account or error, redirect to home
        navigate('/');
      }
    } catch (error) {
      console.error('Error fetching account data:', error);
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const fetchStats = async () => {
    try {
      setStatsLoading(true);
      setError('');

      const token = localStorage.getItem('token');
      const language = localStorage.getItem('i18nextLng') || 'en';

      // Fetch all stats in parallel using GET requests
      const [userStatsRes, planStatsRes, payingUsersRes] = await Promise.all([
        fetch(`/api/admin?action=GET_USER_STATS&period=${datePeriod}`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'X-Language': language
          }
        }).then(res => res.json()),
        fetch(`/api/admin?action=GET_PLAN_STATS&period=${datePeriod}`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'X-Language': language
          }
        }).then(res => res.json()),
        fetch(`/api/admin?action=GET_PAYING_USERS_STATS`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'X-Language': language
          }
        }).then(res => res.json())
      ]);

      if (userStatsRes.success) {
        setUserStats(userStatsRes.stats);
      }
      if (planStatsRes.success) {
        setPlanStats(planStatsRes.stats);
      }
      if (payingUsersRes.success) {
        setPayingUsersStats(payingUsersRes.stats);
      }
    } catch (err) {
      console.error('Error fetching stats:', err);
      setError(t('admin.dashboard.errorLoadingStats'));
    } finally {
      setStatsLoading(false);
    }
  };

  if (loading) {
    return (
      <Container className="text-center py-5">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-3">Loading admin dashboard...</p>
      </Container>
    );
  }

  if (!accountData) {
    return null; // Will redirect
  }

  return (
    <>
      <SlidingMenu title={t('admin.dashboard.title')} />
      
      <Container className="py-4">
        <Row className="mb-4">
          <Col>
            <h1>{t('admin.dashboard.title')}</h1>
            <p className="text-muted">{t('admin.dashboard.welcome', { name: currentUser?.name })}</p>
          </Col>
        </Row>

        {/* Date Filter */}
        <Row className="mb-4">
          <Col>
            <Card>
              <Card.Body>
                <div className="btn-group" role="group">
                  <Button
                    variant={datePeriod === 'today' ? 'primary' : 'outline-primary'}
                    onClick={() => setDatePeriod('today')}
                  >
                    {t('admin.dashboard.filters.today')}
                  </Button>
                  <Button
                    variant={datePeriod === 'week' ? 'primary' : 'outline-primary'}
                    onClick={() => setDatePeriod('week')}
                  >
                    {t('admin.dashboard.filters.week')}
                  </Button>
                  <Button
                    variant={datePeriod === 'month' ? 'primary' : 'outline-primary'}
                    onClick={() => setDatePeriod('month')}
                  >
                    {t('admin.dashboard.filters.month')}
                  </Button>
                  <Button
                    variant={datePeriod === 'all' ? 'primary' : 'outline-primary'}
                    onClick={() => setDatePeriod('all')}
                  >
                    {t('admin.dashboard.filters.all')}
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {error && (
          <Alert variant="danger" className="mb-4">
            {error}
          </Alert>
        )}

        {statsLoading ? (
          <div className="text-center py-5">
            <Spinner animation="border" />
            <p className="mt-3">{t('admin.dashboard.loading')}</p>
          </div>
        ) : (
          <>
            {/* Quick Stats Cards */}
            <Row className="mb-4">
              <Col md={3} className="mb-3">
                <Card className="h-100">
                  <Card.Body className="text-center">
                    <h3 className="text-primary">{userStats?.totalUsers || 0}</h3>
                    <p className="text-muted mb-0">{t('admin.dashboard.stats.totalUsers')}</p>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3} className="mb-3">
                <Card className="h-100">
                  <Card.Body className="text-center">
                    <h3 className="text-success">{userStats?.newUsersInPeriod || 0}</h3>
                    <p className="text-muted mb-0">{t('admin.dashboard.stats.newUsers')}</p>
                    <small className="text-muted">({t(`admin.dashboard.filters.${datePeriod}`)})</small>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3} className="mb-3">
                <Card className="h-100">
                  <Card.Body className="text-center">
                    <h3 className="text-info">{planStats?.totalPlans || 0}</h3>
                    <p className="text-muted mb-0">{t('admin.dashboard.stats.totalPlans')}</p>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3} className="mb-3">
                <Card className="h-100">
                  <Card.Body className="text-center">
                    <h3 className="text-warning">{payingUsersStats?.totalPayingUsers || 0}</h3>
                    <p className="text-muted mb-0">{t('admin.dashboard.stats.payingUsers')}</p>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* Detailed Stats */}
            <Row className="mb-4">
              {/* User Statistics */}
              <Col md={6} className="mb-4">
                <Card>
                  <Card.Header>
                    <h5 className="mb-0">
                      <i className="bi bi-people me-2"></i>
                      {t('admin.dashboard.userStats.title')}
                    </h5>
                  </Card.Header>
                  <Card.Body>
                    {userStats && (
                      <>
                        <p><strong>{t('admin.dashboard.userStats.totalUsers')}:</strong> {userStats.totalUsers}</p>
                        <p><strong>{t('admin.dashboard.userStats.newUsers')}:</strong> {userStats.newUsersInPeriod} ({t(`admin.dashboard.filters.${userStats.period}`)})</p>
                        <p><strong>{t('admin.dashboard.userStats.verified')}:</strong> {userStats.verifiedUsers}</p>
                        <p><strong>{t('admin.dashboard.userStats.unverified')}:</strong> {userStats.unverifiedUsers}</p>
                        <hr />
                        <p><strong>{t('admin.dashboard.userStats.emailPassword')}:</strong> {userStats.emailPasswordUsers}</p>
                        <p><strong>{t('admin.dashboard.userStats.google')}:</strong> {userStats.googleUsers}</p>
                      </>
                    )}
                  </Card.Body>
                </Card>
              </Col>

              {/* Plan Statistics */}
              <Col md={6} className="mb-4">
                <Card>
                  <Card.Header>
                    <h5 className="mb-0">
                      <i className="bi bi-map me-2"></i>
                      {t('admin.dashboard.planStats.title')}
                    </h5>
                  </Card.Header>
                  <Card.Body>
                    {planStats && (
                      <>
                        <p><strong>{t('admin.dashboard.planStats.totalPlans')}:</strong> {planStats.totalPlans}</p>
                        <p><strong>{t('admin.dashboard.planStats.plansCreated')}:</strong> {planStats.plansInPeriod} ({t(`admin.dashboard.filters.${planStats.period}`)})</p>
                        <p><strong>{t('admin.dashboard.planStats.avgPerUser')}:</strong> {planStats.avgPlansPerUser}</p>
                        <p><strong>{t('admin.dashboard.planStats.withSharedLinks')}:</strong> {planStats.plansWithSharedLinks}</p>
                        {planStats.plansByLanguage && planStats.plansByLanguage.length > 0 && (
                          <>
                            <hr />
                            <p><strong>{t('admin.dashboard.planStats.byLanguage')}:</strong></p>
                            <ul>
                              {planStats.plansByLanguage.map((item, idx) => (
                                <li key={idx}>{item.language}: {item.count}</li>
                              ))}
                            </ul>
                          </>
                        )}
                      </>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* Paying Users Statistics */}
            <Row className="mb-4">
              <Col md={6} className="mb-4">
                <Card>
                  <Card.Header>
                    <h5 className="mb-0">
                      <i className="bi bi-credit-card me-2"></i>
                      {t('admin.dashboard.payingUsers.title')}
                    </h5>
                  </Card.Header>
                  <Card.Body>
                    {payingUsersStats && (
                      <>
                        <p><strong>{t('admin.dashboard.payingUsers.total')}:</strong> {payingUsersStats.totalPayingUsers}</p>
                        <p><strong>{t('admin.dashboard.payingUsers.pro')}:</strong> {payingUsersStats.proUsers}</p>
                        <p><strong>{t('admin.dashboard.payingUsers.proPlus')}:</strong> {payingUsersStats.proPlusUsers}</p>
                        <p><strong>{t('admin.dashboard.payingUsers.free')}:</strong> {payingUsersStats.freeUsers}</p>
                        <hr />
                        <p><strong>{t('admin.dashboard.payingUsers.monthly')}:</strong> {payingUsersStats.monthlySubscriptions}</p>
                        <p><strong>{t('admin.dashboard.payingUsers.yearly')}:</strong> {payingUsersStats.yearlySubscriptions}</p>
                      </>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* Currency Management */}
            <Row>
              <Col className="mb-4">
                <CurrencyManagement />
              </Col>
            </Row>
          </>
        )}
      </Container>
      <VersionFooter />
    </>
  );
}

